import { FC, useMemo, useState } from 'react';
import { Add, Close, Save, Foundation, Grass, SquareFoot } from '@mui/icons-material';
import {
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider,
  Radio,
  RadioGroup,
  Autocomplete
} from '@mui/material';
import { useQuery } from 'react-query';
import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  getFoundationTypes,
  getSitePrepBudgetLevels,
  getClearingLevels,
  getSodMeasuringPoints,
  getPlantPackages,
  putJobSitePrepInfo
} from '../../../../fetch';
import { IDropdownResponse, IJobSitePrepInfo } from '../../../../models';
import { Loader, SMBSelect, Card } from '../../../../components';

interface ISitePrep {
  sitePrepInfo: IJobSitePrepInfo;
  isLoadingSitePrepInfo: boolean;
  setDirtyForm: (val: boolean) => void;
  refetchSitePrepInfo: () => void;
}

const SitePrepInfoSchema = Yup.object().shape({
  jobId: Yup.number(),
  foundationType: Yup.string(),
  sitePrepBudget: Yup.string(),
  foundationHeight: Yup.string().typeError('Must be a number').max(4, 'Max 4 characters'),
  landscapingBudgetAmount: Yup.string().max(5, 'Max 5 characters').nullable(),
  isClearingRequired: Yup.string(),
  clearingLevel: Yup.string(),
  clearingWidthDrivewaySide: Yup.string().max(3, 'Max 3 characters').nullable(),
  clearingWidthNonDrivewaySide: Yup.string().max(3, 'Max 3 characters').nullable(),
  clearingDepthBehindHouse: Yup.string().max(3, 'Max 3 characters').nullable(),
  isEntireAreaSodded: Yup.string(),
  howFarSodded: Yup.string().max(3, 'Max 3 characters').nullable(),
  sodMeasuringPoint: Yup.string(),
  maxSodWidthExists: Yup.string(),
  maxSodWidth: Yup.string().max(3, 'Max 3 characters').nullable(),
  basePlantPackage: Yup.string(),
  islandInYard: Yup.string(),
  treeInYard: Yup.string(),
  irrigationStandard: Yup.string(),
  excludedPlantPackages: Yup.array(),
  showClearingWidthDrivewaySide: Yup.boolean()
});

export const SitePrep: FC<ISitePrep> = ({
  sitePrepInfo,
  isLoadingSitePrepInfo,
  setDirtyForm,
  refetchSitePrepInfo
}) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { jobId }: { jobId: string } = useParams();
  const [selectedPlantPackageValues, setSelectedPlantPackageValues] = useState<IDropdownResponse[]>(
    []
  );
  const [inputSelectedPlantPackages, setInputSelectedPlantPackages] = useState('');

  const { isLoading: isLoadingFoundationTypes, data: foundationTypes } = useQuery<
    IDropdownResponse[],
    Error
  >(['getFoundationTypes'], () => getFoundationTypes());

  const { isLoading: isLoadingSitePrepBudgestLevels, data: sitePrepBudgetLevels } = useQuery<
    IDropdownResponse[],
    Error
  >(['getSitePrepBudgetLevels'], () => getSitePrepBudgetLevels());

  const { isLoading: isLoadingClearingLevels, data: clearingLevels } = useQuery<
    IDropdownResponse[],
    Error
  >(['getClearingLevels'], () => getClearingLevels());

  const { isLoading: isLoadingSodMeasuringPoints, data: sodMeasuringPoints } = useQuery<
    IDropdownResponse[],
    Error
  >(['getSodMeasuringPoints'], () => getSodMeasuringPoints());

  const { isLoading: isLoadingPlantPackages, data: plantPackages } = useQuery<any[], Error>(
    ['getPlantPackages'],
    () => getPlantPackages(),
    {
      onSuccess: d => {
        const options = d.filter(record => {
          return sitePrepInfo?.excludedPlantPackages?.includes(record.value);
        });
        setSelectedPlantPackageValues(options);
      },
      enabled: sitePrepInfo !== undefined
    }
  );

  const activeItems = plantPackages?.filter(plantPackage => plantPackage?.isActive);

  selectedPlantPackageValues?.forEach(selectedItem => {
    if (!activeItems.some(item => item.value === selectedItem.value)) {
      activeItems.push(selectedItem);
    }
  });

  const basePlantPackagesData = useMemo(() => activeItems ?? [], [activeItems]);

  return (
    <>
      {isLoadingSitePrepInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingSitePrepInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            ...sitePrepInfo,
            foundationType: sitePrepInfo?.foundationType ?? '',
            sitePrepBudget: sitePrepInfo?.sitePrepBudget ?? '',
            foundationHeight: sitePrepInfo?.foundationHeight ?? '',
            landscapingBudgetAmount: sitePrepInfo?.landscapingBudgetAmount ?? '',
            isClearingRequired:
              sitePrepInfo?.isClearingRequired === true
                ? 'Yes'
                : sitePrepInfo?.isClearingRequired === false
                ? 'No'
                : '',
            clearingLevel: sitePrepInfo?.clearingLevel ?? '',
            clearingWidthDrivewaySide: sitePrepInfo?.clearingWidthDrivewaySide ?? '',
            clearingWidthNonDrivewaySide: sitePrepInfo?.clearingWidthNonDrivewaySide ?? '',
            clearingDepthBehindHouse: sitePrepInfo?.clearingDepthBehindHouse ?? '',
            isEntireAreaSodded:
              sitePrepInfo?.isEntireAreaSodded === true
                ? 'Yes'
                : sitePrepInfo?.isEntireAreaSodded === false
                ? 'No'
                : '',
            howFarSodded: sitePrepInfo?.howFarSodded ?? '',
            sodMeasuringPoint: sitePrepInfo?.sodMeasuringPoint ?? '',
            maxSodWidthExists:
              sitePrepInfo?.maxSodWidthExists === true
                ? 'Yes'
                : sitePrepInfo?.maxSodWidthExists === false
                ? 'No'
                : '',
            maxSodWidth: sitePrepInfo?.maxSodWidth ?? '',
            basePlantPackage: sitePrepInfo?.basePlantPackage ?? '',
            islandInYard:
              sitePrepInfo?.islandInYard === true
                ? 'Yes'
                : sitePrepInfo?.islandInYard === false
                ? 'No'
                : '',
            treeInYard:
              sitePrepInfo?.treeInYard === true
                ? 'Yes'
                : sitePrepInfo?.treeInYard === false
                ? 'No'
                : '',
            irrigationStandard:
              sitePrepInfo?.irrigationStandard === true
                ? 'Yes'
                : sitePrepInfo?.irrigationStandard === false
                ? 'No'
                : '',
            excludedPlantPackages: sitePrepInfo?.excludedPlantPackages ?? []
          }}
          validationSchema={SitePrepInfoSchema}
          onSubmit={async (values, actions) => {
            try {
              const data: IJobSitePrepInfo = {
                ...values,
                isClearingRequired: values.isClearingRequired === 'Yes' ? true : false,
                isEntireAreaSodded: values.isEntireAreaSodded === 'Yes' ? true : false,
                maxSodWidthExists: values.maxSodWidthExists === 'Yes' ? true : false,
                islandInYard: values.islandInYard === 'Yes' ? true : false,
                treeInYard: values.treeInYard === 'Yes' ? true : false,
                irrigationStandard: values.irrigationStandard === 'Yes' ? true : false,
                foundationHeight: Number(values.foundationHeight) ?? null,
                sodMeasuringPoint: values.sodMeasuringPoint ?? null,
                landscapingBudgetAmount: values.landscapingBudgetAmount
                  ? Number(values.landscapingBudgetAmount)
                  : null,
                clearingWidthDrivewaySide: values.clearingWidthDrivewaySide
                  ? Number(values.clearingWidthDrivewaySide)
                  : null,
                clearingWidthNonDrivewaySide: values.clearingWidthNonDrivewaySide
                  ? Number(values.clearingWidthNonDrivewaySide)
                  : null,
                clearingDepthBehindHouse: values.clearingDepthBehindHouse
                  ? Number(values.clearingDepthBehindHouse)
                  : null,
                howFarSodded: values.howFarSodded ? Number(values.howFarSodded) : null,
                maxSodWidth: values.maxSodWidth ? Number(values.maxSodWidth) : null
              };

              await putJobSitePrepInfo(parseInt(jobId), data);
              refetchSitePrepInfo();
              enqueueSnackbar('Site Prep has been updated successfully!', {
                variant: 'success'
              });
            } catch (error) {
              enqueueSnackbar(error?.Detail ?? 'Something went wrong. Plan was not updated.', {
                variant: 'error'
              });
            }
          }}
        >
          {({
            isSubmitting,
            values,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            // This is also what Formik does in their official documentation.
            // https://stackoverflow.com/questions/61031464/setstate-called-in-render-prop-is-causing-a-react-warning
            // Also see https://github.com/jaredpalmer/formik/issues/1218
            if (dirty) {
              setTimeout(() => setDirtyForm(true), 0);
            } else {
              setTimeout(() => setDirtyForm(false), 0);
            }

            return (
              <>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}

                <Form onSubmit={handleSubmit}>
                  <Box mt={1}>
                    <Card title="Foundation" icon={<Foundation />}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <SMBSelect
                                name="foundationType"
                                label="Foundation Type"
                                id="foundationType"
                                variant="outlined"
                                disabled={isLoadingFoundationTypes}
                                value={values.foundationType}
                                onChange={({ target: { value } }) => {
                                  setFieldValue('foundationType', value);
                                }}
                                onBlur={handleBlur}
                              >
                                {foundationTypes &&
                                  foundationTypes.map((foundationType, index) => {
                                    return (
                                      <MenuItem key={`${index}`} value={foundationType.value}>
                                        {foundationType.description}
                                      </MenuItem>
                                    );
                                  })}
                              </SMBSelect>
                            </Grid>
                            {values.foundationType === 'MonolithicSlab' && (
                              <Grid item xs={12} sm={6}>
                                <SMBSelect
                                  name="sitePrepBudget"
                                  label="Site Prep Budget"
                                  id="sitePrepBudget"
                                  variant="outlined"
                                  disabled={isLoadingSitePrepBudgestLevels}
                                  value={values.sitePrepBudget}
                                  onChange={({ target: { value } }) => {
                                    setFieldValue('sitePrepBudget', value);
                                  }}
                                  onBlur={handleBlur}
                                >
                                  {sitePrepBudgetLevels &&
                                    sitePrepBudgetLevels.map((budget, index) => {
                                      return (
                                        <MenuItem key={`${index}`} value={budget.value}>
                                          {budget.description}
                                        </MenuItem>
                                      );
                                    })}
                                </SMBSelect>
                              </Grid>
                            )}
                            {(values.foundationType === 'BlockNFill' ||
                              values.foundationType === 'Crawlspace') && (
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  type="number"
                                  fullWidth
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Foundation Height"
                                  name="foundationHeight"
                                  size="small"
                                  value={values.foundationHeight}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('foundationHeight', e.target.value)}
                                  error={Boolean(
                                    touched.foundationHeight && errors.foundationHeight
                                  )}
                                  helperText={touched.foundationHeight && errors.foundationHeight}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                    <Card title="Clearing" icon={<SquareFoot />}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12} sm={values.isClearingRequired === 'Yes' ? 12 : 6}>
                              <FormControl component="fieldset">
                                <FormLabel>Clearing Required?</FormLabel>
                                <RadioGroup
                                  row
                                  aria-label="isClearingRequired"
                                  name="isClearingRequired"
                                  value={values?.isClearingRequired}
                                  onChange={(e, value) =>
                                    setFieldValue('isClearingRequired', e.target.value)
                                  }
                                >
                                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                  <FormControlLabel value="No" control={<Radio />} label="No" />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                            {values.isClearingRequired === 'Yes' && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    name="clearingLevel"
                                    label="Clearing Level"
                                    id="clearingLevel"
                                    variant="outlined"
                                    disabled={isLoadingClearingLevels}
                                    value={values.clearingLevel}
                                    onChange={({ target: { value } }) => {
                                      setFieldValue('clearingLevel', value);
                                    }}
                                    onBlur={handleBlur}
                                  >
                                    {clearingLevels &&
                                      clearingLevels.map((clearingLevel, index) => {
                                        return (
                                          <MenuItem key={`${index}`} value={clearingLevel.value}>
                                            {clearingLevel.description}
                                          </MenuItem>
                                        );
                                      })}
                                  </SMBSelect>
                                </Grid>
                                {sitePrepInfo.showClearingWidthDrivewaySide && (
                                  <Grid item xs={12} sm={6}>
                                    <TextField
                                      type="number"
                                      fullWidth
                                      variant="outlined"
                                      autoComplete="nope"
                                      label="Over Clear Dimension Driveway Side"
                                      name="clearingWidthDrivewaySide"
                                      size="small"
                                      value={values.clearingWidthDrivewaySide}
                                      onBlur={handleBlur}
                                      onChange={e =>
                                        setFieldValue('clearingWidthDrivewaySide', e.target.value)
                                      }
                                      error={Boolean(
                                        touched.clearingWidthDrivewaySide &&
                                          errors.clearingWidthDrivewaySide
                                      )}
                                      helperText={
                                        touched.clearingWidthDrivewaySide &&
                                        errors.clearingWidthDrivewaySide
                                      }
                                    />
                                  </Grid>
                                )}
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Over Clear Dimension Non-Driveway Side"
                                    name="clearingWidthNonDrivewaySide"
                                    size="small"
                                    value={values.clearingWidthNonDrivewaySide}
                                    onBlur={handleBlur}
                                    onChange={e =>
                                      setFieldValue('clearingWidthNonDrivewaySide', e.target.value)
                                    }
                                    error={Boolean(
                                      touched.clearingWidthNonDrivewaySide &&
                                        errors.clearingWidthNonDrivewaySide
                                    )}
                                    helperText={
                                      touched.clearingWidthNonDrivewaySide &&
                                      errors.clearingWidthNonDrivewaySide
                                    }
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="Over Clear Dimension Behind House"
                                    name="clearingDepthBehindHouse"
                                    size="small"
                                    value={values.clearingDepthBehindHouse}
                                    onBlur={handleBlur}
                                    onChange={e =>
                                      setFieldValue('clearingDepthBehindHouse', e.target.value)
                                    }
                                    error={Boolean(
                                      touched.clearingDepthBehindHouse &&
                                        errors.clearingDepthBehindHouse
                                    )}
                                    helperText={
                                      touched.clearingDepthBehindHouse &&
                                      errors.clearingDepthBehindHouse
                                    }
                                  />
                                </Grid>
                              </>
                            )}
                            {values.isClearingRequired === 'No' && (
                              <Grid item xs={12} sm={6}>
                                <FormControl component="fieldset">
                                  <FormLabel>Is Entire Cleared Area Sodded</FormLabel>
                                  <RadioGroup
                                    row
                                    aria-label="isEntireAreaSodded"
                                    name="isEntireAreaSodded"
                                    value={values?.isEntireAreaSodded}
                                    onChange={(e, value) =>
                                      setFieldValue('isEntireAreaSodded', e.target.value)
                                    }
                                  >
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                    <FormControlLabel value="No" control={<Radio />} label="No" />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                            )}
                            {values.isEntireAreaSodded === 'No' && (
                              <>
                                <Grid item xs={12} sm={6}>
                                  <TextField
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    autoComplete="nope"
                                    label="How Far Behind Measuring Point is Sodded"
                                    name="howFarSodded"
                                    size="small"
                                    value={values.howFarSodded}
                                    onBlur={handleBlur}
                                    onChange={e => setFieldValue('howFarSodded', e.target.value)}
                                    error={Boolean(touched.howFarSodded && errors.howFarSodded)}
                                    helperText={touched.howFarSodded && errors.howFarSodded}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <SMBSelect
                                    name="sodMeasuringPoint"
                                    label="What is the Measuring Point"
                                    id="sodMeasuringPoint"
                                    variant="outlined"
                                    disabled={isLoadingSodMeasuringPoints}
                                    value={values.sodMeasuringPoint}
                                    onChange={({ target: { value } }) => {
                                      setFieldValue('sodMeasuringPoint', value);
                                    }}
                                    onBlur={handleBlur}
                                  >
                                    {sodMeasuringPoints &&
                                      sodMeasuringPoints.map((measuringPoint, index) => {
                                        return (
                                          <MenuItem key={`${index}`} value={measuringPoint.value}>
                                            {measuringPoint.description}
                                          </MenuItem>
                                        );
                                      })}
                                  </SMBSelect>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                  <FormControl component="fieldset">
                                    <FormLabel>Is there a Max Sodding Width</FormLabel>
                                    <RadioGroup
                                      row
                                      aria-label="maxSodWidthExists"
                                      name="maxSodWidthExists"
                                      value={values?.maxSodWidthExists}
                                      onChange={(e, value) =>
                                        setFieldValue('maxSodWidthExists', e.target.value)
                                      }
                                    >
                                      <FormControlLabel
                                        value="Yes"
                                        control={<Radio />}
                                        label="Yes"
                                      />
                                      <FormControlLabel value="No" control={<Radio />} label="No" />
                                    </RadioGroup>
                                  </FormControl>
                                </Grid>
                              </>
                            )}
                            {values.maxSodWidthExists === 'Yes' && (
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  type="number"
                                  fullWidth
                                  variant="outlined"
                                  autoComplete="nope"
                                  label="Max Sodding Width"
                                  name="maxSodWidth"
                                  size="small"
                                  value={values.maxSodWidth}
                                  onBlur={handleBlur}
                                  onChange={e => setFieldValue('maxSodWidth', e.target.value)}
                                  error={Boolean(touched.maxSodWidth && errors.maxSodWidth)}
                                  helperText={touched.maxSodWidth && errors.maxSodWidth}
                                />
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                    <Card title="Landscape Options" icon={<Grass />}>
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                type="number"
                                fullWidth
                                variant="outlined"
                                autoComplete="nope"
                                label="Landscaping Budget Amount"
                                name="landscapingBudgetAmount"
                                size="small"
                                value={values.landscapingBudgetAmount}
                                onBlur={handleBlur}
                                onChange={e =>
                                  setFieldValue('landscapingBudgetAmount', e.target.value)
                                }
                                error={Boolean(
                                  touched.landscapingBudgetAmount && errors.landscapingBudgetAmount
                                )}
                                helperText={
                                  touched.landscapingBudgetAmount && errors.landscapingBudgetAmount
                                }
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <SMBSelect
                                name="basePlantPackage"
                                label="Base Plant Package"
                                id="basePlantPackage"
                                variant="outlined"
                                disabled={isLoadingPlantPackages}
                                value={values.basePlantPackage}
                                onChange={({ target: { value } }) => {
                                  setFieldValue('basePlantPackage', value);
                                }}
                                onBlur={handleBlur}
                              >
                                {plantPackages &&
                                  (() => {
                                    const activeItems = plantPackages.filter(
                                      plantPackage => plantPackage.isActive
                                    );

                                    const selectedItem = plantPackages.find(
                                      plantPackage => plantPackage.value === values.basePlantPackage
                                    );

                                    if (
                                      selectedItem &&
                                      !selectedItem.isActive &&
                                      !activeItems.includes(selectedItem)
                                    ) {
                                      activeItems.push(selectedItem);
                                    }

                                    return activeItems.map((plantPackage, index) => (
                                      <MenuItem key={`${index}`} value={plantPackage.value}>
                                        {plantPackage.description}
                                      </MenuItem>
                                    ));
                                  })()}
                              </SMBSelect>
                            </Grid>
                            <Grid item xs={12}>
                              <Autocomplete
                                multiple
                                id="excludedPlantPackages"
                                value={selectedPlantPackageValues}
                                onChange={(event, newValue: any[]) => {
                                  setSelectedPlantPackageValues(newValue);
                                  setFieldValue(
                                    'excludedPlantPackages',
                                    newValue.map(val => val.value)
                                  );
                                }}
                                inputValue={inputSelectedPlantPackages}
                                onInputChange={(event, newInputValue) => {
                                  setInputSelectedPlantPackages(newInputValue);
                                }}
                                options={basePlantPackagesData}
                                getOptionLabel={option => option.description}
                                filterSelectedOptions
                                renderOption={(props, option: any) => {
                                  return (
                                    <li {...props} key={`${option.value}`}>
                                      {option.description}
                                    </li>
                                  );
                                }}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    disabled={isLoadingPlantPackages}
                                    label={
                                      isLoadingPlantPackages
                                        ? 'Loading...'
                                        : 'Excluded Plant Packages'
                                    }
                                    placeholder="Plant Packages"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                              <FormControl component="fieldset">
                                <FormLabel>Island in Yard</FormLabel>
                                <RadioGroup
                                  row
                                  aria-label="islandInYard"
                                  name="islandInYard"
                                  value={values?.islandInYard}
                                  onChange={(e, value) =>
                                    setFieldValue('islandInYard', e.target.value)
                                  }
                                >
                                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                  <FormControlLabel value="No" control={<Radio />} label="No" />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                            {values.islandInYard === 'No' && (
                              <Grid item xs={12} sm={3}>
                                <FormControl component="fieldset">
                                  <FormLabel>Tree in Yard</FormLabel>
                                  <RadioGroup
                                    row
                                    aria-label="treeInYard"
                                    name="treeInYard"
                                    value={values?.treeInYard}
                                    onChange={(e, value) =>
                                      setFieldValue('treeInYard', e.target.value)
                                    }
                                  >
                                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                    <FormControlLabel value="No" control={<Radio />} label="No" />
                                  </RadioGroup>
                                </FormControl>
                              </Grid>
                            )}
                            <Grid item xs={12} sm={3}>
                              <FormControl component="fieldset">
                                <FormLabel>Irrigation Standard</FormLabel>
                                <RadioGroup
                                  row
                                  aria-label="irrigationStandard"
                                  name="irrigationStandard"
                                  value={values?.irrigationStandard}
                                  onChange={(e, value) =>
                                    setFieldValue('irrigationStandard', e.target.value)
                                  }
                                >
                                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                                  <FormControlLabel value="No" control={<Radio />} label="No" />
                                </RadioGroup>
                              </FormControl>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Box>
                  <Box mt={1} mb={0.5}>
                    <Divider />
                  </Box>
                  <ButtonGroup>
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={jobId ? <Save /> : <Add />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        history.push('/development/jobs');
                      }}
                    >
                      Cancel
                    </Button>
                  </ButtonGroup>
                </Form>
              </>
            );
          }}
        </Formik>
      )}
    </>
  );
};
